'use client'
import { useState } from "react";
import Link from "next/link";
import axios from "axios";
import { Dropdown } from 'primereact/dropdown';
import Button from "@/ui/button";
import Checkbox from "@/ui/checkbox";
import cl from "clsx";
import ModalContinue from "@/components/elements/modal-continue/modal-continue";

import styles from './styles.module.css';
import './contact-form.css';

export default function ContactForm({ dict, showComment = true }) {
    // Destructure the needed text from dict.contactForm
    const {
        placeholders,
        questionTypePlaceholder,
        questionOptions,
        checkboxLabelMobile,
        termsAndConditions,
        checkboxLabelDesktop,
        privacyPolicy,
        buttonText,
        alertNotChecked,
        successMessage,
        errorMessage,
        errorAlert
    } = dict.contactForm;

    const [name, setName] = useState('');
    const [phoneNumber, setPhoneNumber] = useState('');
    const [email, setEmail] = useState('');
    const [questionType, setQuestionType] = useState('');
    const [comment, setComment] = useState('');
    const [isChecked, setIsChecked] = useState(false);
    const [confirmPhone, setConfirmPhone] = useState('');

    const [modalVisible, setModalVisible] = useState(false);
    const [modalTitle, setModalTitle] = useState('');
    const [modalText, setModalText] = useState('');

    const validateEmail = (email) => {
        return String(email)
            .toLowerCase()
            .match(
                /^(([^<>()[\]\\.,;:\s@"]+(\.[^<>()[\]\\.,;:\s@"]+)*)|(".+"))@((\[[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\])|(([a-zA-Z\-0-9]+\.)+[a-zA-Z]{2,}))$/
            );
    };

    const validatePhone = (phone) => {
        return String(phone)
            .match(/^[+]?[(]?[0-9]{3}[)]?[-\s.]?[0-9]{3}[-\s.]?[0-9]{4,6}$/);
    };

    const handleSubmit = async (event) => {
        event.preventDefault();
        const apiUrl = (process.env.NEXT_PUBLIC_API_URL || '').replace(/\/$/, '');

        if (!isChecked) {
            setModalTitle(dict.contactFormContactPage?.titles?.termsAndConditions || 'Terms and Conditions');
            setModalText(alertNotChecked);
            setModalVisible(true);
            return;
        }

        if (!validateEmail(email)) {
            setModalTitle(dict.contactFormContactPage?.titles?.error || 'Error');
            setModalText(dict.contactForm.invalidEmail || 'Please enter a valid e-mail address.');
            setModalVisible(true);
            return;
        }

        if (!validatePhone(phoneNumber)) {
            setModalTitle(dict.contactFormContactPage?.titles?.error || 'Error');
            setModalText(dict.contactForm.invalidPhone || 'Please enter a valid phone number.');
            setModalVisible(true);
            return;
        }

        if (showComment && !comment.trim()) {
            setModalTitle(dict.contactFormContactPage?.titles?.error || 'Error');
            setModalText(dict.contactForm.invalidComment || 'Please enter a comment.');
            setModalVisible(true);
            return;
        }

        try {
            const response = await axios.post(
                `${apiUrl}/api/base/contact/`,
                {
                    name,
                    email,
                    phone: phoneNumber,
                    question_type: questionType,
                    comment: comment,
                    confirm_phone: confirmPhone,
                },
                {
                    headers: {
                        'Content-Type': 'application/json',
                        accept: 'application/json'
                    }
                }
            );

            if (response.status === 200) {
                setModalTitle(dict.contactFormContactPage?.titles?.thankYou || 'Thank you!');
                setModalText(successMessage);
                setModalVisible(true);
                setName('');
                setPhoneNumber('');
                setEmail('');
                setQuestionType('');
                setComment('');
                setIsChecked(false);
            } else {
                setModalTitle(dict.contactFormContactPage?.titles?.error || 'Error');
                setModalText(errorMessage);
                setModalVisible(true);
            }
        } catch (error) {
            console.error("Error sending message:", error);
            setModalTitle(dict.contactFormContactPage?.titles?.error || 'Error');
            setModalText(errorAlert);
            setModalVisible(true);
        }
    };

    return (
        <form className={cl(styles.contact_form, 'contact_form')} onSubmit={handleSubmit}>
            <div className="extra_contact_row" aria-hidden="true">
                <input
                    type="text"
                    name="confirm_phone"
                    value={confirmPhone}
                    onChange={(e) => setConfirmPhone(e.target.value)}
                    autoComplete="off"
                    tabIndex={-1}
                />
            </div>
            <div className={styles.contact_form_wrapper}>
                <input
                    id="name"
                    className={styles.contact_form__input}
                    type="text"
                    placeholder={placeholders.name}
                    value={name}
                    onChange={(e) => setName(e.target.value)}
                    required
                />

                <input
                    id="phone"
                    className={styles.contact_form__input}
                    type="tel"
                    placeholder={placeholders.phone}
                    value={phoneNumber}
                    onChange={(e) => setPhoneNumber(e.target.value)}
                    required
                />

                <input
                    id="email"
                    className={styles.contact_form__input}
                    type="email"
                    placeholder={placeholders.email}
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    required
                />

                <Dropdown
                    id="question-type"
                    value={questionType}
                    options={questionOptions}
                    onChange={(e) => setQuestionType(e.value)}
                    placeholder={questionTypePlaceholder}
                />

                {showComment && (
                    <textarea
                        id="comment"
                        className={styles.contact_form__textarea}
                        placeholder={dict.contactFormContactPage?.placeholders?.comment || 'Comment*'}
                        value={comment}
                        onChange={(e) => setComment(e.target.value)}
                        required
                    />
                )}

                {/* Mobile checkbox block */}
                <div className={styles.contact_form__checkbox_block_mobile}>
                    <Checkbox
                        checked={isChecked}
                        onChange={() => setIsChecked(!isChecked)}
                    />
                    <label htmlFor="contact-form-checkbox">
                        {checkboxLabelMobile}
                        <Link href="https://ic-ces.at/compliance-policy" className={styles.contact_form__link}>
                            {termsAndConditions}
                        </Link>.
                    </label>
                </div>

                <div className={styles.contact_form__button_wrapper}>
                    <Button typeButton="submit" type="submit" title={buttonText} />
                </div>
            </div>

            <ModalContinue
                visible={modalVisible}
                setVisible={setModalVisible}
                title={modalTitle}
                text={modalText}
                dict={dict}
            />

            {/* Desktop checkbox block */}
            <div className={styles.contact_form__checkbox_block}>
                <Checkbox
                    checked={isChecked}
                    onChange={() => setIsChecked(!isChecked)}
                />
                <label htmlFor="contact-form-checkbox">
                    {checkboxLabelDesktop.includes('{link}') ? (
                        <>
                            {checkboxLabelDesktop.split('{link}')[0]}
                            <Link
                                href="https://ic-ces.at/compliance-policy"
                                target="_blank"
                                className={styles.contact_form__link}
                            >
                                {privacyPolicy}
                            </Link>
                            {checkboxLabelDesktop.split('{link}')[1]}
                        </>
                    ) : (
                        <>
                            {checkboxLabelDesktop}
                            <Link
                                href="https://ic-ces.at/compliance-policy"
                                target="_blank"
                                className={styles.contact_form__link}
                            >
                                {privacyPolicy}
                            </Link>
                        </>
                    )}
                </label>
            </div>
        </form>
    );
}
